<script setup lang="ts">
import { computed, onBeforeUnmount, ref } from 'vue'
import Fab from '~/components/ui/SourceFab.vue'
import SourceButton from '~/components/ui/SourceButton.vue'
import SourceTable from '~/components/ui/SourceTable.vue'
import type { EquipeContactRow } from '~/composables/equipe/useCreateEquipeForm'
import { useSourceToast } from '~/composables/ui/useSourceToast'

const rows = defineModel<EquipeContactRow[]>('rows', { required: true })

defineProps<{
  loading: boolean
}>()

const sourceTableRef = ref<{
  startEdit: () => void
  deleteRow: (row: EquipeContactRow) => void
  hasAnyEmptyEditableRow: boolean | { value: boolean }
} | null>(null)

const lastDeletedContact = ref<{ row: EquipeContactRow; index: number } | null>(null)
const showUndoBanner = ref(false)
const undoBannerTimer = ref<ReturnType<typeof setTimeout> | null>(null)
const toast = useSourceToast()

const columns = [
  { key: 'conLib', label: 'Intitulé', placeholder: 'Ex. Fixe' },
  { key: 'conChamp', label: 'Contact', placeholder: 'Ex. 01 23 45 67 89' },
]

function resolveExposedBoolean(exposed: unknown): boolean {
  if (typeof exposed === 'boolean') return exposed
  if (exposed && typeof exposed === 'object' && 'value' in exposed) {
    return Boolean((exposed as { value: boolean }).value)
  }
  return false
}

const hasEmptyEditableRow = computed(() => {
  return resolveExposedBoolean(sourceTableRef.value?.hasAnyEmptyEditableRow)
})

function onAddContactClick() {
  if (hasEmptyEditableRow.value) {
    toast.warning(
      'Contact incomplet',
      "Valide ou supprime d’abord la ligne de contact vide avant d’en ajouter une nouvelle.",
      7000,
    )
    return
  }
  sourceTableRef.value?.startEdit()
}

function clearUndoBannerTimer() {
  if (!undoBannerTimer.value) return
  clearTimeout(undoBannerTimer.value)
  undoBannerTimer.value = null
}

function hideUndoBanner() {
  showUndoBanner.value = false
  lastDeletedContact.value = null
  clearUndoBannerTimer()
}

function armUndoBanner(timeoutMs = 8000) {
  showUndoBanner.value = true
  clearUndoBannerTimer()
  undoBannerTimer.value = setTimeout(function expireUndoBanner() {
    hideUndoBanner()
  }, timeoutMs)
}

function undoLastContactDelete() {
  if (!lastDeletedContact.value) return
  const deletedContact = lastDeletedContact.value
  const currentContacts = [...(rows.value ?? [])]
  const safeIndex = Math.max(0, Math.min(deletedContact.index, currentContacts.length))
  currentContacts.splice(safeIndex, 0, deletedContact.row)
  rows.value = currentContacts
  hideUndoBanner()
}

function removeRow(rowIndex: number) {
  const currentContacts = [...(rows.value ?? [])]
  if (rowIndex < 0 || rowIndex >= currentContacts.length) return
  const row = currentContacts[rowIndex]
  if (!row) return
  lastDeletedContact.value = {
    row: { conLib: row.conLib ?? '', conChamp: row.conChamp ?? '' },
    index: rowIndex,
  }
  currentContacts.splice(rowIndex, 1)
  rows.value = currentContacts
  armUndoBanner()
}

const tableActions = [
  {
    icon: 'delete',
    title: 'Supprimer',
    onClick: function removeContact(_row: EquipeContactRow, rowIndex: number) {
      removeRow(rowIndex)
    },
  },
]

onBeforeUnmount(function clearContactUndoTimer() {
  clearUndoBannerTimer()
})

defineExpose({ hideUndoBanner })
</script>

<template>
  <div class="section-field">
    <p class="h6">Informations de contact</p>

    <SourceTable
      ref="sourceTableRef"
      v-model:rows="rows"
      :columns="columns"
      :actions="tableActions"
    />

    <Fab
      icon="add"
      text="Ajouter une adresse de contact"
      size="medium"
      :disabled="loading"
      class="fab-container"
      @click="onAddContactClick"
    />
  </div>

  <div v-if="showUndoBanner" class="undo-banner" role="status" aria-live="polite">
    <span class="undo-banner__text">Contact supprimé.</span>
    <SourceButton size="small" text="Annuler" type="deleteTertiary" @click="undoLastContactDelete" />
  </div>
</template>

<style scoped lang="scss">
@use "~/assets/scss/partials/components/entity-form" as *;
@use "~/assets/scss/partials/elpdl/colors" as c;
@use "~/assets/scss/partials/elpdl/index" as *;
@use "~/assets/scss/app" as *;

.section-field {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 8px;
}

.h6 {
  margin: 0;
  color: c.$enedis-primary-500;
  @extend .h6;
}

.undo-banner {
  position: fixed;
  left: 50%;
  bottom: 16px;
  transform: translateX(-50%);
  z-index: 13000;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: $spacing-xs $spacing-xs;
  border-radius: 8px;
  background: var(--color-bg-elevated);
  box-shadow: var(--color-shadow-panel);
  border: 1px solid c.$enedis-neutral-60;
  width: min(560px, calc(100vw - 24px));
}

.undo-banner__text {
  font-size: 14px;
  color: var(--color-text);
}
</style>
